import { WEAPONS } from "./weapons.js";
const weaponName = (p) => WEAPONS[p.weapon]?.displayName ?? p.weapon;
const COL_K = { label: "K", width: "2.6em", value: (p) => String(p.kills) };
const COL_D = { label: "D", width: "2.6em", value: (p) => String(p.deaths) };
const COL_TIER = { label: "TIER", width: "3.4em", value: (p) => String(p.tier + 1) };
// which gun you finished on is live information — at match end it is noise in
// a 380px panel, so it is `wide` and the compact board drops it
const COL_WEAPON = { label: "WEAPON", width: "9em", wide: true, value: weaponName };
const STANDINGS = {
    gungame: {
        title: "LADDER STANDINGS",
        columns: [COL_TIER, COL_WEAPON, COL_K, COL_D],
        // the ladder is the mode; the tiebreak rewards whoever got there FIRST
        rank: (a, b) => b.tier - a.tier ||
            (a.tierChangedMs ?? Infinity) - (b.tierChangedMs ?? Infinity) ||
            b.kills - a.kills,
    },
    royale: {
        title: "BATTLE ROYALE",
        columns: [
            { label: "LIVES", width: "3.4em", value: (p) => String(p.lives) },
            COL_K, COL_D,
        ],
        // survival IS the mode: contenders (alive or holding a life) outrank the
        // eliminated; then plain deathmatch order. No tier — royale has no ladder.
        rank: (a, b) => ((b.alive || b.lives > 0) ? 1 : 0) - ((a.alive || a.lives > 0) ? 1 : 0) ||
            b.kills - a.kills ||
            a.deaths - b.deaths,
    },
    ffa: {
        title: "DEATHMATCH",
        columns: [
            COL_K,
            COL_D,
            { label: "+/-", width: "3.2em", value: (p) => (p.kills - p.deaths > 0 ? "+" : "") + (p.kills - p.deaths) },
        ],
        rank: (a, b) => b.kills - a.kills || a.deaths - b.deaths,
    },
    arena: {
        title: "BOB-OMB BLAST",
        columns: [{ label: "⭐", width: "3.2em", value: (p) => String(p.stars) }, COL_K, COL_D],
        rank: (a, b) => b.stars - a.stars || b.kills - a.kills,
    },
    treasure: {
        title: "TREASURE HUNT",
        columns: [
            { label: "BANKED", width: "4.6em", value: (p) => `${p.treasureSecs}s` },
            COL_K,
            COL_D,
        ],
        rank: (a, b) => b.treasureSecs - a.treasureSecs || b.kills - a.kills,
    },
    plunder: {
        title: "FLAGS",
        // `treasureSecs` is reused as the DELIVERY COUNT here (see tickPlunder) —
        // the field is a spare uint16 in every non-treasure mode and PlayerState is
        // at 61 of Colyseus' 64 fields, so adding a parallel counter was not free.
        columns: [
            { label: "TEAM", width: "4.2em", value: (p) => p.team === 1 ? "RED" : p.team === 2 ? "BLUE" : "—" },
            { label: "CAP", width: "3.2em", value: (p) => `${p.treasureSecs}` },
            COL_K,
            COL_D,
        ],
        rank: (a, b) => b.treasureSecs - a.treasureSecs || b.kills - a.kills,
    },
    relay: {
        title: "IMPERION RELAY",
        // `treasureSecs` is reused as the BEAM-KILL COUNT here (see the relay
        // branch in GunGameRoom.onDeath) — same spare-uint16 dodge as plunder.
        columns: [
            { label: "BEAMS", width: "4.2em", value: (p) => `${p.treasureSecs}` },
            COL_K,
            COL_D,
        ],
        rank: (a, b) => b.treasureSecs - a.treasureSecs || b.kills - a.kills || a.deaths - b.deaths,
    },
    onegun: {
        title: "ONE GUN",
        // `treasureSecs` is reused as the GUN-KILL COUNT here (see the onegun
        // branch in GunGameRoom.onDeath) — same spare-uint16 dodge as plunder and
        // relay. The winner is whoever lands the final-rung gun kill; a timeout
        // ranks by gun kills (time holding the gun without killing earns nothing).
        columns: [
            { label: "GUN", width: "3.6em", value: (p) => `${p.treasureSecs}` },
            COL_K,
            COL_D,
        ],
        rank: (a, b) => b.treasureSecs - a.treasureSecs || b.kills - a.kills || a.deaths - b.deaths,
    },
    horde: {
        title: "HORDEBEARER",
        // `treasureSecs` is reused as the HORDE-KILL COUNT here (claw kills your
        // horde landed while you held the skull — see the zombie-claw attribution
        // in GunGameRoom.applyDamage) — same spare-uint16 dodge as plunder/relay/
        // onegun. `kills` already INCLUDES horde kills (they credit the bearer),
        // so the rank is simply total frags; the column exists for legibility.
        columns: [
            { label: "HORDE", width: "4.2em", value: (p) => `${p.treasureSecs}` },
            COL_K,
            COL_D,
        ],
        rank: (a, b) => b.kills - a.kills || b.treasureSecs - a.treasureSecs || a.deaths - b.deaths,
        // THE HORDE is team 2 (`spawnZombie`), humans are team 0. Same rule as
        // purge: zombies are real `state.players` entries and must never be drawn
        // on a board or crowned by `timeoutWin`.
        hidden: (p) => p.team === 2,
    },
    burnout: {
        title: "BURNOUT STANDINGS",
        columns: [
            COL_TIER,
            { label: "KILLS", width: "3.6em", value: (p) => String(p.kills) },
            { label: "BURN", width: "3.2em", value: (p) => String(p.burnouts) },
            COL_D,
        ],
        rank: (a, b) => b.tier - a.tier || b.kills - a.kills || a.burnouts - b.burnouts,
    },
    purge: {
        title: "SURVIVORS",
        columns: [
            { label: "POINTS", width: "4.6em", value: (p) => String(p.points) },
            { label: "KILLS", width: "3.4em", value: (p) => String(p.kills) },
            { label: "LIVES", width: "3.4em", value: (p) => String(p.lives) },
        ],
        rank: (a, b) => b.points - a.points || b.kills - a.kills,
        // THE HORDE is team 2 (`spawnZombie`), survivors are team 1. Zombies are
        // real entries in `state.players`, so without this the purge scoreboard is
        // twenty "ZOMBIE" rows — and a timed-out purge match could crown one.
        hidden: (p) => p.team === 2,
    },
};
// DESCENT (reverse Gun Game) ranks exactly like the ladder — higher tier is
// still closer to winning (you're deeper down the power curve, nearer the final
// knife rung) — it just carries its own board title.
STANDINGS.descent = { ...STANDINGS.gungame, title: "DESCENT STANDINGS" };
/** Standings rule for `mode`. An unknown mode degrades to the gungame ladder
 *  rather than throwing, so a version-skewed client still draws a board. */
export function standingsFor(mode) {
    return (mode && STANDINGS[mode]) || STANDINGS.gungame;
}
/** Is this player ranked and drawn at all? Pending players and BODY DOUBLE
 *  holo-clones never are. */
export function standingVisible(mode, p) {
    if (p.pending || p.decoy)
        return false;
    return !standingsFor(mode).hidden?.(p);
}
/** Columns for the narrow end-of-round board (drops the `wide` ones). */
export function compactColumns(mode) {
    return standingsFor(mode).columns.filter((c) => !c.wide);
}
/** `< 0` = `a` ranks above `b`, under `mode`'s rule. */
export function rankStandings(mode, a, b) {
    return standingsFor(mode).rank(a, b);
}
/** ARENA (Bob-omb Blast) SUDDEN-DEATH star target — the net-star lead that
 *  instantly ends a match. It SCALES with the field so a big free-for-all plays
 *  a real battle instead of the old sub-minute sprint to 4 (2-4 karts ending at
 *  +4 net stars was "the way you score feels wrong"), while a 1v1 stays snappy
 *  at 3. `alive` is the non-pending, non-decoy scoring roster (both server sites
 *  and the client HUD compute that census identically). When a round timer is
 *  set — the default — `GunGameRoom.timeoutWin` still crowns the MOST-stars
 *  player at the clock (arena `rank` sorts by stars), so this cap only closes
 *  out a blowout early; the mode reads as MK's "most stars when time's up" with
 *  a sudden-death mercy rule. The `stars` schema caps at 20, so the max-8 return
 *  always fits. SINGLE SOURCE OF TRUTH: the server's two scoring sites
 *  (arenaBombHit + onDeath) and the client's star HUD all call this, so the
 *  "FIRST TO N" number can never disagree with the win the server awards. */
export function arenaWinTarget(alive) {
    // Mario Kart: Double Dash!! Bob-omb Blast (decomp RaceMgr): first to 3
    // points with 2 karts, 4 with 3+. (Was min(8, alive+1) — a Neon invention.)
    return alive <= 2 ? 3 : 4;
}
//# sourceMappingURL=standings.js.map